import React from 'react';
import { Link } from 'react-router-dom';
import { Code, Smartphone, Cloud, Shield, Search, Users, BarChart, Settings } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

/**
 * ServicesPage component - Comprehensive services showcase
 * Author: VB Entreprise
 * 
 * Features:
 * - Detailed service categories
 * - Technology stack displays
 * - Process methodology
 * - Pricing information
 */
const ServicesPage: React.FC = () => {
  const serviceCategories = [
    {
      title: 'IT Development',
      icon: <Code className="w-8 h-8" />,
      description: 'Custom software solutions built with cutting-edge technologies',
      services: [
        {
          name: 'Web Development',
          description: 'Responsive, scalable web applications using React, Node.js, and modern frameworks',
          technologies: ['React', 'Node.js', 'TypeScript', 'MongoDB'],
        },
        {
          name: 'Mobile App Development',
          description: 'Native and cross-platform mobile applications for iOS and Android',
          technologies: ['React Native', 'Flutter', 'Swift', 'Kotlin'],
        },
        {
          name: 'Custom Software',
          description: 'Tailored enterprise software solutions for specific business needs',
          technologies: ['Python', 'Java', '.NET', 'PostgreSQL'],
        }
      ]
    },
    {
      title: 'Digital Marketing',
      icon: <BarChart className="w-8 h-8" />,
      description: 'Data-driven marketing strategies that deliver measurable results',
      services: [
        {
          name: 'SEO & SEM',
          description: 'Search engine optimization and marketing to increase visibility',
          technologies: ['Google Analytics', 'SEMrush', 'Ahrefs', 'Google Ads'],
        },
        {
          name: 'Social Media Marketing',
          description: 'Comprehensive social media strategy and content management',
          technologies: ['Hootsuite', 'Buffer', 'Canva', 'Facebook Ads'],
        },
        {
          name: 'Content Marketing',
          description: 'Strategic content creation and distribution for brand awareness',
          technologies: ['WordPress', 'HubSpot', 'Mailchimp', 'Analytics'],
        }
      ]
    },
    {
      title: 'Cloud Solutions',
      icon: <Cloud className="w-8 h-8" />,
      description: 'Secure, scalable cloud infrastructure and migration services',
      services: [
        {
          name: 'Cloud Migration',
          description: 'Seamless migration of existing systems to cloud platforms',
          technologies: ['AWS', 'Azure', 'Docker', 'Kubernetes'],
        },
        {
          name: 'DevOps Services',
          description: 'Continuous integration and deployment pipeline setup',
          technologies: ['Jenkins', 'GitLab CI', 'Terraform', 'Ansible'],
        },
        {
          name: 'Infrastructure Management',
          description: '24/7 monitoring and management of cloud infrastructure',
          technologies: ['CloudWatch', 'Datadog', 'Prometheus', 'Grafana'],
        }
      ]
    },
    {
      title: 'Managed IT',
      icon: <Settings className="w-8 h-8" />,
      description: 'Comprehensive IT support and infrastructure management',
      services: [
        {
          name: '24/7 IT Support',
          description: 'Round-the-clock technical support and helpdesk services',
          technologies: ['ServiceNow', 'Zendesk', 'TeamViewer', 'Slack'],
        },
        {
          name: 'Cybersecurity',
          description: 'Advanced security measures and threat protection',
          technologies: ['Firewalls', 'Antivirus', 'VPN', 'Security Audits'],
        },
        {
          name: 'Network Management',
          description: 'Complete network infrastructure setup and maintenance',
          technologies: ['Cisco', 'Juniper', 'WiFi 6', 'Network Monitoring'],
        }
      ]
    }
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Discovery & Analysis',
      description: 'We analyze your requirements, goals, and current infrastructure to create a tailored strategy.'
    },
    {
      step: '02',
      title: 'Planning & Design',
      description: 'Our team develops detailed project plans, wireframes, and technical specifications.'
    },
    {
      step: '03',
      title: 'Development & Implementation',
      description: 'We build and implement solutions using agile methodologies and best practices.'
    },
    {
      step: '04',
      title: 'Testing & Quality Assurance',
      description: 'Rigorous testing ensures reliability, security, and optimal performance.'
    },
    {
      step: '05',
      title: 'Deployment & Support',
      description: 'We deploy your solution and provide ongoing support and maintenance.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Services | VB Entreprise | Premium IT & Digital Marketing Agency for US, UK & Canada Businesses</title>
      </Helmet>
      <div className="pt-8">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-blue-50 to-indigo-100 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
              Our <span className="text-blue-600">Services</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Comprehensive IT and digital marketing solutions designed to accelerate your business growth and digital transformation.
            </p>
            <Link
              to="/contact"
              className="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors inline-block"
            >
              Request a Quote
            </Link>
          </div>
        </section>

        {/* Service Categories */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {serviceCategories.map((category, categoryIndex) => (
              <div key={categoryIndex} className="mb-20">
                <div className="text-center mb-12">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4 text-blue-600">
                    {category.icon}
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">{category.title}</h2>
                  <p className="text-xl text-gray-600 max-w-2xl mx-auto">{category.description}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {category.services.map((service, serviceIndex) => (
                    <div key={serviceIndex} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">{service.name}</h3>
                      <p className="text-gray-600 mb-6">{service.description}</p>
                      
                      <div className="mb-6">
                        <h4 className="text-sm font-medium text-gray-900 mb-2">Technologies:</h4>
                        <div className="flex flex-wrap gap-2">
                          {service.technologies.map((tech, techIndex) => (
                            <span
                              key={techIndex}
                              className="px-3 py-1 bg-blue-100 text-blue-700 text-xs rounded-full"
                            >
                              {tech}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="flex justify-between items-center">
                        <Link
                          to="/contact"
                          className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                        >
                          Get Quote
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Process Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Process</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                We follow a proven methodology to ensure successful project delivery and client satisfaction.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
              {processSteps.map((step, index) => (
                <div key={index} className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 text-white rounded-full text-xl font-bold mb-4">
                    {step.step}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600 text-sm">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-blue-600">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold text-white mb-4">
              Ready to Start Your Project?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Contact us today for a free consultation and custom quote for your specific requirements.
            </p>
            <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <Link
                to="/contact"
                className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                Get Free Consultation
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ServicesPage;